import moment from "moment";

import { api } from "../config/variables";
import { dp_costing_keys } from "../variables/general";

import {
	cloneDeepWith,
	compact,
	get,
	isEmpty,
	isPlainObject,
	toString,
} from "./barrels/lodash";

let basename = "/";

// Resolves after `ms` milliseconds. Used to space out back-to-back API calls.
export const delay = (ms) =>
	new Promise((resolve) => {
		setTimeout(resolve, ms);
	});

export const createCookie = (
	name,
	value,
	expTimeReq,
	expiryTime,
	path = "/",
	source = false,
	decode = false
) => {
	if (source) {
		setCrossSubdomainCookie(name, value, 3, decode);
	} else {
		const maxAge = expTimeReq ? `; max-age=${expiryTime}` : "";
		document.cookie = `${name}=${value}${maxAge}; path=${path}; SameSite=Lax`;
	}
};

const getDomain = () => {
	const host = window.location.hostname;

	if (host === "localhost" || host === "127.0.0.1") {
		return host;
	}

	const parts = host.split(".");
	return parts.slice(-2).join(".");
};

function setCrossSubdomainCookie(name, value, days, decode) {
	try {
		const encodedValue = decode ? value : encodeURIComponent(value);

		const d = new Date();
		d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);

		const expires = `expires=${d.toUTCString()}`;
		const path = "path=/";
		const domain = `domain=${getDomain()}`;

		document.cookie = `${name}=${encodedValue}; ${expires}; ${path}; ${domain}; SameSite=None; Secure`;
	} catch (err) {
		console.error(err);
	}
}

export const readCookie = (name, showParsed) => {
	let cookieVal = document.cookie
		.split(";")
		.find((item) => item.trim().startsWith(name));
	cookieVal = cookieVal && cookieVal.replace(`${name}=`, "");
	if (showParsed && cookieVal) {
		return JSON.parse(cookieVal);
	} else {
		return cookieVal && cookieVal.trim();
	}
};

export const clearAllCookies = () => {
	const cookies = document.cookie.split(";");

	cookies.forEach((cookie) => {
		const cookieName = cookie.split("=")[0].trim();
		document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
	});
};

export const eraseCookie = (name) => {
	createCookie(name, "", true, -1, "/");
};

export const getValidCookie = (name) => {
	const cookie = document.cookie;
	const cookArr = cookie.split(";");
	let bearerTokens = cookArr.map((c) => {
		if (c.includes("bearer")) {
			return c.trim().replace(`${name}=`, "");
		}
		return "";
	});
	bearerTokens = compact(bearerTokens);
	return bearerTokens[0];
};

const ABD_AUTO_COOKIE = "abd_auto_redirect";
const ABD_REDIRECT_TTL = 900; // seconds (15 min)

export const storeAbdRedirectIntent = () => {
	const params = new URLSearchParams(window.location.search);
	const autoAbd = params.get("auto_abd");
	if (autoAbd === "true") {
		createCookie(
			ABD_AUTO_COOKIE,
			"true",
			true,
			ABD_REDIRECT_TTL,
			"/",
			true,
			true
		);
	}
};

export const getAbdRedirectUrl = (defaultUrl) => {
	const autoAbd = readCookie(ABD_AUTO_COOKIE);
	if (autoAbd === "true") {
		eraseCookie(ABD_AUTO_COOKIE);
		return `/client/appointmentdelivery?auto_abd=true`;
	}
	return defaultUrl;
};

export const reAuthenticateUser = () => {
	const currentTime = moment().utc();
	let expiryTime = readCookie("expires");
	expiryTime = moment(expiryTime);
	const minutesToExpiry = expiryTime.diff(currentTime, "minutes");
	return minutesToExpiry <= 10;
};

export const getParameterByNameAndType = (name, type, url) => {
	let match;
	switch (type) {
		case "hash":
			match = RegExp(`[#&]${name}=([^&]*)`).exec(window.location[type]);
			break;
		case "search":
			match = RegExp(`[?&]${name}=([^&]*)`).exec(window.location[type]);
			break;
		default:
			match = RegExp(`[?&]${name}=([^&]*)`).exec(url[type]);
			break;
	}
	return match && decodeURIComponent(match[1].replace(/\+/g, " "));
};

const generateRandomString = () => {
	const randomString =
		Math.random().toString(36).substring(2, 15) +
		Math.random().toString(36).substring(2, 15);
	return randomString;
};

export const goToLoginPage = () => {
	const randomStr = generateRandomString();
	const queryParams = new URLSearchParams(window.location.search);
	let url = `${api.sso_url}/login?source=rb`;
	if (queryParams) {
		url = `${url}&${queryParams}`;
	}
	return { randomStr, url };
};

export const getSource = () => {
	let source = readCookie("source");
	source = source === "null" ? "sr" : source;
	return source;
};

export const logoutUser = () => {
	const url =
		getSource() === "pr"
			? `${api.sso_pickr_url}`
			: `${api.sso_url}/logout?source=rb`;
	return url;
};

export const support_mail_according_to_source = () => {
	let default_support_email = "cargosupport@shiprocket.com";
	if (getSource() === "pr") {
		default_support_email = "cargosupport@shiprocket.com";
	}
	return default_support_email;
};

export const convertObjectValuesToString = (obj) =>
	cloneDeepWith(obj, (value) => {
		if (!isPlainObject(value)) {
			return toString(value);
		}
		return undefined;
	});

export const reloadUser = () => {
	eraseCookie("bearer");
	eraseCookie("expires");
	eraseCookie("client");
};

export const formatCostingData = (field, value) => {
	if (dp_costing_keys.currency.includes(field)) {
		if (value === null || value === undefined || value === "") {
			return "-";
		}

		const parsedValue = parseFloat(value);
		if (isNaN(parsedValue)) {
			return "-";
		}

		const formatted = parsedValue.toLocaleString("en-IN", {
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
			style: "currency",
			currency: "INR",
		});

		return formatted.replace("-err", "");
	}

	if (dp_costing_keys.weight.includes(field)) {
		return value ? `${value.toString().replace("-err", "")} kg.` : "-";
	}

	if (dp_costing_keys.date.includes(field)) {
		return moment(value?.toString().replace("-err", "")).format("DD-MM-YYYY");
	}

	return value ? value.toString().replace("-err", "") : value;
};

const convertInchToCm = (length_in_inch) =>
	Math.round(2.54 * length_in_inch * 100 + Number.EPSILON) / 100;

export const calculateVolumetricWt = (
	{ length, width, height, units, unit },
	cft = 6
) => {
	let l = length;
	let w = width;
	let h = height;

	if (unit === "inch") {
		l = convertInchToCm(l);
		w = convertInchToCm(w);
		h = convertInchToCm(h);
	}

	if (!l || !w || !h) {
		return 0;
	}

	return ((l * w * h * cft) / 27000) * units;
};

export const toCurrency = (
	value,
	indianNumberFormat = false,
	removeFraction
) => {
	if (!isNaN(value)) {
		const result = parseFloat(value).toLocaleString("en-IN", {
			minimumFractionDigits: removeFraction ? 0 : 2,
			maximumFractionDigits: removeFraction ? 0 : 2,
			style: !indianNumberFormat ? "currency" : undefined,
			currency: !indianNumberFormat ? "INR" : undefined,
		});
		return result.replace("₹", "₹ ");
	} else {
		return 0;
	}
};

export const checkIfAnyKeyInObjectHasValue = (obj) =>
	Object.values(obj).some((el) => Boolean(el));

export const handleScrollToTop = () => {
	document.querySelector("#app-header").scrollIntoView({
		behavior: "smooth",
	});
};

export const locationTypeHandler = (working) => {
	const odas = ["ODA", "ODA-A", "ODA-B", "ODA-C", "ODA-D", "ODA-1", "ODA-2"];
	if (!isEmpty(working)) {
		if (working?.recipient_location_type || working?.sender_location_type) {
			if (
				odas.includes(working.recipient_location_type) ||
				odas.includes(working.sender_location_type)
			) {
				return "ODA";
			} else if (
				[
					working.recipient_location_type,
					working.sender_location_type,
				].includes("OPA")
			) {
				return "OPA";
			} else if (working.sender_location_type.includes("safextension")) {
				return working.sender_location_type;
			} else if (working.recipient_location_type.includes("safextension")) {
				return working.recipient_location_type;
			} else {
				return "Regular";
			}
		} else {
			return "-";
		}
	}
};

export const fromLocationTypeHandler = (working) => {
	const odas = [
		"OPA",
		"OPA2",
		"OPA-1",
		"OPA-2",
		"ODA",
		"ODA-A",
		"ODA-B",
		"ODA-C",
		"ODA-D",
		"ODA-1",
		"ODA-2",
	];
	if (!isEmpty(working)) {
		if (working?.sender_location_type) {
			if (odas.includes(working.sender_location_type)) {
				return "OPA";
			} else if (working.sender_location_type.includes("safextension")) {
				return working.sender_location_type;
			} else {
				return "Regular";
			}
		} else {
			return "-";
		}
	}
};

export const toLocationTypeHandler = (working) => {
	const odas = ["ODA", "ODA-A", "ODA-B", "ODA-C", "ODA-D", "ODA-1", "ODA-2"];
	if (!isEmpty(working)) {
		if (working?.recipient_location_type) {
			if (odas.includes(working.recipient_location_type)) {
				return "ODA";
			} else if (working.recipient_location_type.includes("safextension")) {
				return working.recipient_location_type;
			} else {
				return "Regular";
			}
		} else {
			return "-";
		}
	}
};

export const scrollToElement = (ele) => {
	document.querySelector(ele).scrollIntoView();
};

export const getClientID = (client) => {
	if (client?.role === "customer") {
		return get(client, "customer.client.id", undefined);
	}
	return get(client, "client_poc.client.id", undefined);
};

export const isCustomerUser = () => {
	const client = readCookie("client", true);
	return get(client, "is_customer_user");
};

export const ScrolltoTop = () => {
	window.sideContent?.current?.scrollTo({
		top: 0,
		behavior: "smooth",
	});
};

export const setBasename = () => {
	basename = "/";
};

export const getBasename = () => {
	const client = readCookie("client", true) || "";
	if (client?.customer?.client?.client_name) {
		let client_slug = client.customer.client.client_name;
		client_slug = client_slug.replaceAll(" ", "-").toLowerCase();
		basename = `/${client_slug}`;
	}
	if (client?.client_poc?.client?.client_name) {
		let client_slug = client.client_poc.client.client_name;
		client_slug = client_slug.replaceAll(" ", "-").toLowerCase();
		basename = `/${client_slug}`;
	}
	return basename;
};

export const getToday = () => moment();

export const getKycStatus = () => {
	const client = readCookie("client", true);
	const kycStatus = get(client, "client_poc.client.status", undefined);
	if (kycStatus === "pending") {
		return false;
	} else {
		return true;
	}
};

export const tConv24 = (time24) => {
	let H;
	let h;
	let ampm;
	let ts = time24;
	if (ts) {
		H = Number(ts.substr(0, 2));
		h = H % 12 || 12;
		h = h < 10 ? `0${h}` : h; // leading 0 at the left for 1 digit hours
		ampm = H < 12 ? " AM" : " PM";
		ts = h + ts.substr(2, 3) + ampm;
	}

	return ts;
};

export const Stringify = (data) => {
	const stringify_value = JSON.stringify(data);
	return stringify_value;
};

export const ParseStringifyData = (data) => {
	let parsed_value = data;
	if (typeof data === "string" && !data.includes("-") && data) {
		parsed_value = JSON.parse(data);
	}
	return parsed_value;
};

export const check_client_on_smart_plan = (client_accessed_by_admin = null) => {
	let clientsmartplan = null;
	const is_staff = readCookie("client", true).is_staff;
	if (is_staff) {
		clientsmartplan = client_accessed_by_admin?.rb_plan_name;
	} else {
		const client = readCookie("client", true);
		clientsmartplan = client?.client_poc?.client?.rb_plan_name;
	}
	return clientsmartplan === 1;
};

export const notification_url = () => {
	const client = readCookie("client", true);
	const url = `${api.socket_url}${`${getSource()}_${client?.id}`}/`;
	return url;
};
export const parent_wallet_type_pr_check = () => {
	let temp = false;
	const client = readCookie("client", true)?.client_poc?.client;
	if (getSource() === "pr" && client?.parent_wallet_type) {
		temp = client.parent_wallet_type === "wallet";
	} else if (
		(getSource() === "pr" && client?.parent_wallet_type === null) ||
		(getSource() === "pr" && client?.parent_wallet_type === "null")
	) {
		temp = false;
	} else {
		temp = true;
	}
	return temp;
};

export const check_courier_reassignment_permission = () => {
	let client = readCookie("client", true);
	client = client?.client_poc?.client;
	return !client?.is_courier_reassigment_allowed;
};

export const checkVASPermissions = (key) => {
	if (key) {
		let client_ = readCookie("client", true);
		if (client_.is_staff) {
			return true;
		}
		client_ = client_?.client_poc?.client;
		return Boolean(client_[key]);
	}
	return true;
};

export const handleTabClosing = () => {
	const as_user_cookie = readCookie("as_user_id");
	if (as_user_cookie) {
		eraseCookie("client");
		eraseCookie("bearer");
		eraseCookie("expires");
		setCrossSubdomainCookie("as_user_id", "", -1);
	}
};

export const srPlanType = () => {
	let client = readCookie("client", true);
	client = client?.client_poc?.client;
	if (client?.sr_id) {
		if (client?.sr_plan_name) {
			return `(${client?.sr_plan_name})`;
		} else {
			return "(Basic)";
		}
	}
};

export const rbPlanType = () => {
	let client = readCookie("client", true);
	client = client?.client_poc?.client;
	if (client?.rb_plan_name) {
		if (client?.rb_plan_name === "1") {
			return "(Advantage)";
		}
	} else {
		return "(Basic)";
	}
};

export const stringNumber = (val, convert_to_rupee = true) => {
	const num = parseFloat(val);
	if (!isNaN(num)) {
		if (num <= 0) {
			return num;
		}
		return convert_to_rupee ? toCurrency(num) : num;
	}
	return val;
};

export const formatDateToYYYYMMDD = (date) => {
	if (!(date instanceof Date) || isNaN(date.getTime())) {
		return null;
	}

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

export const removeCookie = (name, path = "/", domain) => {
	try {
		let cookieStr = `${name}=; Max-Age=-1; path=${path};`;
		if (domain) {
			cookieStr += `domain=${domain};`;
		}
		document.cookie = cookieStr;
	} catch (error) {
		console.error("Error removing cookie:", error);
	}
};
